import Accordion from "@/common/components/Accordion";
import CreateMaintenanceSpecificationModal from "./CreateMaintenanceSpecificationModal";
import { EstablishmentLeadData, MaintenanceSpecificationDirectiveKlass } from "../../../types/establishmentLead.type";
import { AlertCircleIcon, ChevronDownCircleIcon } from "lucide-react";
import UpdateMaintenanceSpecificationModal from "./UpdateMaintenanceSpecificationModal";
import DeleteMaintenanceSpecificationModal from "./DeleteMaintenanceSpecificationModal";
import { maintenanceSpecificationDirectiveKlassLabel } from "@/features/establishmentLeadsFunnel/constants/maintenanceSpecificationDirectiveKlassLabel.constant";
import Alert from "@/common/components/Alert";

interface MaintenanceSpecificationsSectionProps {
  directiveKlass: MaintenanceSpecificationDirectiveKlass;
  establishmentLead: EstablishmentLeadData;
  alertText: {
    title: string;
    description: string;
  };
  disabled?: boolean;
  allowUpdate?: boolean;
}

const MaintenanceSpecificationValueItem = (props: { label: string; value: string }) => {
  return (
    <div className="flex items-center gap-3">
      <div>
        <p className="italic font-light">
          {props.label}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <ChevronDownCircleIcon
          strokeWidth={1}
          className="text-white-300"
        />

        <div>
          <p className="font-bold">
            {props.value}
          </p>
        </div>
      </div>
    </div>
  );
}

const MaintenanceSpecificationsSection = ({
  directiveKlass,
  establishmentLead,
  alertText,
  disabled,
  allowUpdate,
}: MaintenanceSpecificationsSectionProps) => {
  return (
    <Accordion
      defaultOpen
      head={(
        <div className="flex items-center justify-between gap-2 w-full mr-2">
          <p>
            {maintenanceSpecificationDirectiveKlassLabel[directiveKlass]}
          </p>

          {allowUpdate && (
            <CreateMaintenanceSpecificationModal
              directiveKlass={directiveKlass}
              establishmentLead={establishmentLead}
              alertText={alertText}
              disabled={disabled}
            />
          )}
        </div>
      )}
      className="p-4"
      triggerClassName="px-0 py-1 text-[18px] font-bold"
      bodyClassName="pt-4 pb-0"
      body={(
        <div className="flex flex-col gap-4">
          <Alert
            variant="warning"
            Icon={AlertCircleIcon}
            containerClassName="bg-transparent p-0 flex-1"
          >
            <div className="flex flex-col">
              <p className="text-xs">
                {alertText.title}
              </p>

              <p className="text-xs italic font-light">
                {alertText.description}
              </p>
            </div>
          </Alert>

          {establishmentLead.maintenance_specifications
            ?.filter(({ directive_klass }) => directive_klass === directiveKlass)
            .map((maintenanceSpecification) => (
              <div
                key={maintenanceSpecification.id}
                className="bg-white-100 p-4 rounded-md flex flex-col gap-3"
              >
                <div className="flex items-start justify-between">
                  <div className="flex flex-col gap-2 flex-1">
                    <MaintenanceSpecificationValueItem
                      label="Categorias do equipamento"
                      value={maintenanceSpecification.equipment_category_specifications?.map(
                        ({ category }) => category.display_name
                      )?.join(', ') || '--'}
                    />

                    <MaintenanceSpecificationValueItem
                      label="Fabricantes"
                      value={maintenanceSpecification.equipment_manufacturer_specifications?.map(
                        ({ manufacturer }) => manufacturer.display_name
                      )?.join(', ') || '--'}
                    />

                    <MaintenanceSpecificationValueItem
                      label="Sistemas de manutenção atendidos"
                      value={maintenanceSpecification.system_specifications?.map(
                        ({ system }) => system.display_name
                      )?.join(', ') || '--'}
                    />
                  </div>

                  {allowUpdate && (
                    <div className="flex items-center gap-3">
                      <UpdateMaintenanceSpecificationModal
                        maintenanceSpecification={maintenanceSpecification}
                        establishmentLead={establishmentLead}
                        alertText={alertText}
                        disabled={disabled}
                      />

                      <DeleteMaintenanceSpecificationModal
                        maintenanceSpecification={maintenanceSpecification}
                        establishmentLead={establishmentLead}
                        disabled={disabled}
                      />
                    </div>
                  )}
                </div>
              </div>
            ))}
        </div>
      )}
    />
  );
}

export default MaintenanceSpecificationsSection;
